import React, { useMemo, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import { Layout, Button, Tooltip } from 'antd'
import { DownloadOutlined } from '@ant-design/icons'
import Grid from '../Grid'
import GridControlsCardContainer from '../GridControlsCardContainer'
import GridContextualToolbar from './GridContextualToolbar'
import { FeeValidationErrorTypes } from '../constants/Validation'
import withMemoization from '../../../common/components/MemoWrapper'
import useGridColumns from '../hooks/useGridColumns'
import { useGridState } from '../hooks/useGridState'
import { useValidationPillsConfig } from '../hooks/useValidationPillsConfig'
import { useRowData } from '../hooks/useRowData'
import { useGridUndoRedo } from '../hooks/useGridUndoRedo'
import LoadingSpinner from './LoadingSpinner'
import '../styles/Grid.scss'
import '../styles/GridContainer.scss'
import '../styles/GridShared.scss'

const GridContainer = ({
  initialRowData,
  onSave,
  overrideType,
  overrideName,
  backButton,
  hideSaveButton = false,
  selectedFields = [],
  mode = 'create',
  sheetId,
}) => {
  const gridApiRef = useRef(null)
  const { undo, redo, undoSize, redoSize, recordEdit, resetValidation, canValidate } = useGridUndoRedo(gridApiRef)

  const { validatedRows, validationSummary, refreshValidation } = useRowData({
    initialRowData,
    overrideType,
    selectedFields,
    gridApiRef,
  })

  // Handle grid-specific state
  const {
    filteredRows,
    activeFilters,
    isSummaryExpanded,
    canSaveOverride,
    isSaving,
    currentEditingRow,
    addRow,
    handleToggleDelete,
    handleHeaderDeleteToggle,
    handleFilterChanged,
    handleFilteredRowsChange,
    handleRemoveFilter,
    handleToggleExpand,
    handleValidationFilterChange,
    saveGridData,
    handleExportCSV,
    handleExportXLSX,
    handleCellValueChanged,
    handleToolbarValidate,
    handleCellEditingStarted,
    handleCellEditingStopped,
    restoreColumnState,
    initializeGridState,
  } = useGridState({
    validatedRows,
    gridApiRef,
    overrideType,
    overrideName,
    fieldsToOverride: selectedFields,
    undoSize,
    refreshValidation,
    resetValidation,
    mode,
    sheetId,
    initialRowData,
  })

  // Move onGridReady from Grid.jsx to here
  const onGridReady = useCallback(
    (params) => {
      if (gridApiRef) {
        gridApiRef.current = params.api
        // Save initial column state using the function from useGridState
        initializeGridState()
      }
    },
    [gridApiRef, initializeGridState],
  )

  // Handle save button click
  const handleSaveClick = async () => {
    const result = await saveGridData()
    if (result.success) {
      onSave(result.result)
    }
  }

  const validationPillsConfig = useValidationPillsConfig(overrideType, selectedFields)

  // Grid context - combine both context objects into one
  const gridContext = useMemo(
    () => ({
      onToggleDelete: handleToggleDelete,
      refreshValidation: refreshValidation,
      recordEdit: recordEdit,
      isRowEditing: !!currentEditingRow,
      currentEditingRow,
      restoreColumnState, // Add to context so cell editors can access it
    }),
    [handleToggleDelete, refreshValidation, currentEditingRow, recordEdit, restoreColumnState],
  )

  // Configure grid columns
  const { columnDefs, defaultColDef } = useGridColumns({
    onHeaderDeleteToggle: handleHeaderDeleteToggle,
    gridContext,
    overrideType,
    selectedFields,
  })

  // Note: We're using single-click editing via the singleClickEdit: true setting in defaultColDef
  // Row editing is now initiated when the user clicks on a cell directly
  // The floating action buttons will appear when a row is in edit mode

  return (
    <div className="grid-manager">
      {isSaving ? (
        <LoadingSpinner text="Saving overrides..." className="saving-spinner" />
      ) : (
        <>
          <GridContextualToolbar
            onValidate={handleToolbarValidate}
            onUndo={() => {
              undo()
              resetValidation(true)
            }}
            onRedo={() => {
              redo()
              resetValidation(true)
            }}
            undoSize={undoSize}
            redoSize={redoSize}
            canValidate={canValidate}
            rowNumber={currentEditingRow}
            onAddRow={addRow}
          />
          <Layout className="content-layout">
            <Grid
              rowData={filteredRows}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              context={gridContext}
              onGridReady={onGridReady}
              onFilterChanged={handleFilterChanged}
              onCellValueChanged={handleCellValueChanged}
              onCellEditingStarted={handleCellEditingStarted}
              onCellEditingStopped={handleCellEditingStopped}
            />
            <GridControlsCardContainer
              isExpanded={isSummaryExpanded}
              onToggleExpand={handleToggleExpand}
              rowData={filteredRows}
              validationSummary={validationSummary}
              onFilteredRowsChange={handleFilteredRowsChange}
              activeFilters={activeFilters}
              onRemoveFilter={handleRemoveFilter}
              validationConfig={validationPillsConfig}
              validationErrorTypes={FeeValidationErrorTypes}
              onValidationFilterChange={handleValidationFilterChange}
            />
          </Layout>
          <div className="save-button-row">
            <div className="download-button">
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={handleExportCSV}
                disabled={!filteredRows || filteredRows.length === 0}
                style={{ marginRight: '8px' }}
              >
                Export CSV
              </Button>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={handleExportXLSX}
                disabled={!filteredRows || filteredRows.length === 0}
              >
                Export XLSX
              </Button>
            </div>
            <div className="action-buttons">
              {backButton && backButton}
              {!hideSaveButton &&
                (canValidate ? (
                  <Tooltip title="Please validate changes before saving" placement="top">
                    <Button
                      id="save-override-button"
                      type="primary"
                      onClick={handleSaveClick}
                      disabled={!canSaveOverride || canValidate}
                      loading={isSaving}
                    >
                      Save Override
                    </Button>
                  </Tooltip>
                ) : (
                  <Button
                    id="save-override-button"
                    type="primary"
                    onClick={handleSaveClick}
                    disabled={!canSaveOverride || canValidate}
                    loading={isSaving}
                  >
                    Save Override
                  </Button>
                ))}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

GridContainer.propTypes = {
  initialRowData: PropTypes.array.isRequired,
  onSave: PropTypes.func.isRequired,
  overrideType: PropTypes.string.isRequired,
  overrideName: PropTypes.string.isRequired,
  hideSaveButton: PropTypes.bool,
  selectedFields: PropTypes.arrayOf(PropTypes.string),
  backButton: PropTypes.element,
  mode: PropTypes.oneOf(['create', 'edit']),
  sheetId: PropTypes.string,
}

export default withMemoization(GridContainer, [
  'initialRowData',
  'overrideType',
  'overrideName',
  'selectedFields',
  'activeFilters',
])
